import LegalDoc from "../components/LegalDoc";

const CONTENT = `# Privacy Policy

**RoofNuts LLC — RoofNuts Calling Portal**

**Effective:** August 1, 2026

RoofNuts LLC ("RoofNuts," "we," "us") operates the RoofNuts Calling Portal at roofnutscalling.com
(the "Portal"), a private business application used by RoofNuts staff and RoofNuts' contractor
clients. This policy explains what information the Portal handles and how we treat it.

The Portal is not open to the public. Accounts are created by RoofNuts administrators; there is no
self-registration.

## Information we collect

**Account information.** Name, business email address, phone number, company name, and role
(administrator, caller, billing administrator, or client). Passwords are stored only as salted
hashes. We record sign-in timestamps.

**Appointment and call records.** When our callers conduct outreach on a client's behalf, they log
the outcome. These records can include the name, phone number, and property address of the property
owner or contact ("Homeowner Information"), the appointment date and time, campaign details, and
notes about the call. A client can see the records associated with their own campaigns.

**Call recordings.** Calls are recorded only when a recording is requested. When a recording is
made, it is stored securely and made available to the applicable client through the Portal.

**Location check-ins.** Where GPS check-in data is provided for a scheduled appointment, we store
the check-in and associate it with the appointment address.

**Messages.** Support chat messages and SMS messages sent or received through the Portal, including
the phone numbers involved and message content.

**Billing information.** Invoice numbers, amounts, balances, due dates, and payment status
synchronized from QuickBooks Online. **We do not collect, process, or store payment card numbers,
bank account numbers, or any cardholder data.** Payment collection is handled entirely by our
payment provider.

**Files and technical data.** Files uploaded to the Portal, plus standard server logs (IP address,
browser type, timestamps) used for security and troubleshooting.

## How we use information

We use this information to operate the Portal: authenticating users, running and reporting on
calling campaigns, scheduling appointments, providing clients visibility into their own campaigns,
sending service notifications, generating and tracking invoices, providing support, and securing and
improving the service.

**We do not sell personal information, and we do not use it for advertising.**

## QuickBooks Online

The Portal connects to RoofNuts' own QuickBooks Online company through Intuit's authorized OAuth 2.0
process. A RoofNuts administrator must explicitly approve the connection.

- **What we access:** customers, service items, invoices, and payment records in RoofNuts' QuickBooks
  company.
- **What we do with it:** create invoices for services, and read invoice and payment status so staff
  and clients can see whether an invoice is open, partially paid, or paid.
- **What we store:** invoice identifiers, document numbers, amounts, balances, due dates, and payment
  amounts and dates.
- **What we never do:** we do not capture payments, and we never access or store payment card or bank
  account data. Payment collection is performed by our separate payment provider.
- **Access credentials:** OAuth tokens are stored on our servers only, are never exposed to a web
  browser, and are transmitted over encrypted connections.
- **Disconnecting:** an administrator can disconnect QuickBooks from the Portal at any time, which
  revokes our access.

We do not share QuickBooks data with any third party except the service providers listed below that
host or transmit it on our behalf.

## Service providers

We share information only with providers that help us run the Portal, and only as needed:

| Provider | Purpose |
|---|---|
| MongoDB | Application database |
| Railway | Backend application hosting |
| Vercel | Website hosting |
| Google (Sheets API) | Campaign and appointment data synchronization |
| Intuit (QuickBooks Online) | Invoicing and payment status |
| Biller Genie | Invoice delivery and payment collection |
| Twilio | SMS messaging |
| Mailgun | Transactional email |
| DigitalOcean (Spaces) | File and recording storage |
| Slack | Internal staff notifications |

We may also disclose information if required by law, to protect our rights or the safety of others,
or in connection with a business transfer.

## Security

We protect information using encrypted connections (HTTPS/TLS), hashed passwords, role-based access
controls that limit each user to the data their role requires, and server-side storage of all
credentials and access tokens. No system is perfectly secure, but we take these safeguards
seriously.

## Retention

We keep information for as long as needed to provide the service and to meet legal, tax, and
accounting obligations. Account records are retained while the account is active. Campaign,
appointment, billing, and recording records are retained per our business and recordkeeping
requirements and then deleted or de-identified.

## Your choices

Portal users may request access to, correction of, or deletion of their account information by
contacting us. Property owners and other individuals whose information appears in appointment
records may contact us to request access to, correction of, or deletion of that information, and we
will respond consistent with applicable law and our obligations to our clients.

Depending on where you live, you may have additional rights over your personal information. Contact
us and we will honor those rights as the law requires.

## Children

The Portal is a business tool and is not directed to anyone under 18. We do not knowingly collect
information from children.

## Changes

We may update this policy. When we do, we will revise the effective date above and post the updated
version at this URL.

## Contact

**RoofNuts LLC**

Registered in the State of Georgia

admin@roofnuts.com
`;

const PrivacyPolicy = () => <LegalDoc content={CONTENT} />;

export default PrivacyPolicy;
